/**
 * NumberBondSVG — Phase 1, Component 2 of the TeachBud Representation Catalog
 *
 * Renders a number bond (part-part-whole diagram) per §5.1 Addition & Subtraction:
 *   - Whole circle at top (or center depending on orientation)
 *   - Two part circles below, connected by lines
 *   - Null values render as blank circles with a dashed stroke — student fills in
 *   - Grades K–2
 */

import React from 'react';

export interface NumberBondProps {
  /** The "whole" value. null = blank (student fills in). */
  whole?: number | null;
  /** First part. null = blank. */
  part1?: number | null;
  /** Second part. null = blank. */
  part2?: number | null;
  /** When true, all three circles are blank regardless of value props. */
  studentMode?: boolean;
  /** Override pixel width. Scales proportionally. */
  width?: number;
  /** Optional CSS class. */
  className?: string;
}

// ── Layout constants ──────────────────────────────────────────────────────────
const VB_W = 280;
const VB_H = 190;

// Circle positions
const WHOLE_CX = 140;
const WHOLE_CY =  48;
const WHOLE_R  =  38;

const PART_CY  = 152;
const PART1_CX =  68;
const PART2_CX = 212;
const PART_R   =  32;

// Line endpoints (from whole circle edge to part circle edge)
const LINE_START_Y = WHOLE_CY + WHOLE_R;  // bottom of whole circle

// ── Colors ────────────────────────────────────────────────────────────────────
const WHOLE_FILL   = '#DBEAFE';  // blue-100
const WHOLE_STROKE = '#3B82F6';  // blue-500
const PART_FILL    = '#FEF9C3';  // yellow-100
const PART_STROKE  = '#EAB308';  // yellow-500
const BLANK_FILL   = '#F9FAFB';  // gray-50
const BLANK_STROKE = '#9CA3AF';  // gray-400
const LINE_COLOR   = '#6B7280';  // gray-500
const TEXT_COLOR   = '#1E3A5F';  // dark navy

export function NumberBondSVG({
  whole = null,
  part1 = null,
  part2 = null,
  studentMode = false,
  width,
  className = '',
}: NumberBondProps) {
  const showWhole = !studentMode && whole !== null && whole !== undefined;
  const showPart1 = !studentMode && part1 !== null && part1 !== undefined;
  const showPart2 = !studentMode && part2 !== null && part2 !== undefined;

  const wholeFill   = showWhole ? WHOLE_FILL   : BLANK_FILL;
  const wholeStroke = showWhole ? WHOLE_STROKE : BLANK_STROKE;
  const part1Fill   = showPart1 ? PART_FILL    : BLANK_FILL;
  const part1Stroke = showPart1 ? PART_STROKE  : BLANK_STROKE;
  const part2Fill   = showPart2 ? PART_FILL    : BLANK_FILL;
  const part2Stroke = showPart2 ? PART_STROKE  : BLANK_STROKE;

  const svgW = width ?? VB_W;
  const svgH = width ? Math.round((VB_H / VB_W) * width) : VB_H;

  // Compute angle for lines from whole to parts
  // Line from (WHOLE_CX, LINE_START_Y) toward (PART_CX, PART_CY - PART_R)
  function lineEnd(partCX: number) {
    const dx = partCX - WHOLE_CX;
    const dy = PART_CY - PART_R - LINE_START_Y;
    const len = Math.sqrt(dx * dx + dy * dy);
    return {
      x2: WHOLE_CX + (dx / len) * (len),
      y2: LINE_START_Y + (dy / len) * (len),
    };
  }

  const line1End = lineEnd(PART1_CX);
  const line2End = lineEnd(PART2_CX);

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={svgW}
      height={svgH}
      className={className}
      role="img"
      aria-label={
        studentMode
          ? 'Number bond — student fills in'
          : `Number bond: ${whole ?? '?'} = ${part1 ?? '?'} + ${part2 ?? '?'}`
      }
    >
      <title>Number bond</title>

      {/* Connecting lines */}
      <line
        x1={WHOLE_CX} y1={LINE_START_Y}
        x2={line1End.x2} y2={line1End.y2}
        stroke={LINE_COLOR} strokeWidth={2.5} strokeLinecap="round"
      />
      <line
        x1={WHOLE_CX} y1={LINE_START_Y}
        x2={line2End.x2} y2={line2End.y2}
        stroke={LINE_COLOR} strokeWidth={2.5} strokeLinecap="round"
      />

      {/* Whole circle */}
      <circle
        cx={WHOLE_CX} cy={WHOLE_CY} r={WHOLE_R}
        fill={wholeFill}
        stroke={wholeStroke}
        strokeWidth={2}
        strokeDasharray={showWhole ? undefined : '5 3'}
      />
      {showWhole && (
        <text
          x={WHOLE_CX} y={WHOLE_CY + 7}
          textAnchor="middle"
          fontSize={24}
          fontFamily="Nunito, sans-serif"
          fontWeight={800}
          fill={TEXT_COLOR}
        >
          {whole}
        </text>
      )}

      {/* Part 1 circle */}
      <circle
        cx={PART1_CX} cy={PART_CY} r={PART_R}
        fill={part1Fill}
        stroke={part1Stroke}
        strokeWidth={2}
        strokeDasharray={showPart1 ? undefined : '5 3'}
      />
      {showPart1 && (
        <text
          x={PART1_CX} y={PART_CY + 7}
          textAnchor="middle"
          fontSize={22}
          fontFamily="Nunito, sans-serif"
          fontWeight={800}
          fill={TEXT_COLOR}
        >
          {part1}
        </text>
      )}

      {/* Part 2 circle */}
      <circle
        cx={PART2_CX} cy={PART_CY} r={PART_R}
        fill={part2Fill}
        stroke={part2Stroke}
        strokeWidth={2}
        strokeDasharray={showPart2 ? undefined : '5 3'}
      />
      {showPart2 && (
        <text
          x={PART2_CX} y={PART_CY + 7}
          textAnchor="middle"
          fontSize={22}
          fontFamily="Nunito, sans-serif"
          fontWeight={800}
          fill={TEXT_COLOR}
        >
          {part2}
        </text>
      )}

      {/* "Whole" label */}
      <text
        x={WHOLE_CX} y={WHOLE_CY - WHOLE_R - 6}
        textAnchor="middle"
        fontSize={11}
        fontFamily="Inter, sans-serif"
        fontWeight={600}
        fill="#6B7280"
        letterSpacing={1}
      >
        WHOLE
      </text>

      {/* "Part" labels */}
      <text
        x={PART1_CX} y={PART_CY + PART_R + 16}
        textAnchor="middle"
        fontSize={11}
        fontFamily="Inter, sans-serif"
        fontWeight={600}
        fill="#6B7280"
        letterSpacing={1}
      >
        PART
      </text>
      <text
        x={PART2_CX} y={PART_CY + PART_R + 16}
        textAnchor="middle"
        fontSize={11}
        fontFamily="Inter, sans-serif"
        fontWeight={600}
        fill="#6B7280"
        letterSpacing={1}
      >
        PART
      </text>
    </svg>
  );
}

// ── Utility: parse a number bond from imageHint text ─────────────────────────
/**
 * Returns true if this imageHint should be rendered as a NumberBondSVG.
 */
export function isNumberBondHint(hint: string): boolean {
  return /number.?bond|part.?part.?whole|part.*whole/i.test(hint);
}

/**
 * Extracts part/whole values from an imageHint.
 * "number bond: 10 = 7 + 3" → { whole: 10, part1: 7, part2: 3 }
 * "number bond showing 5 and 3" → { whole: 8, part1: 5, part2: 3 }
 */
export function parseNumberBondHint(hint: string): { whole: number | null; part1: number | null; part2: number | null } {
  const nums = hint.match(/\d+/g)?.map(Number) ?? [];
  if (nums.length >= 3) return { whole: nums[0], part1: nums[1], part2: nums[2] };
  if (nums.length === 2) return { whole: nums[0] + nums[1], part1: nums[0], part2: nums[1] };
  if (nums.length === 1) return { whole: nums[0], part1: null, part2: null };
  return { whole: null, part1: null, part2: null };
}
